import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Popover,
  Divider,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { DateRangePicker, Calendar } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { CustomThemeType } from "../../context/Providers";
import DashboardLayout from "../../layouts/DashboardLayout";
import type { Order, OrderSnapshot, Truck } from "../../types/DB2";
import { useTrucksData } from "../../context/TrucksDataProvider";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { format } from "date-fns";
import styled from "@emotion/styled";
import { useShipmentManager } from "../../context/ShipmentManagerProvider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import {
  DragDropContext,
  Droppable,
  Draggable,
  OnDragEndResponder,
} from "@hello-pangea/dnd";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import TruckIcon from "@mui/icons-material/LocalShipping";
import { useUsersByManufacturer } from "../../context/UsersDataFetcher";
import { useUserData } from "../../context/UserDataFetcher";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";
// Compare by cabinet number within an order (numeric rank), then by label, then id
const cabinetRank = (value?: string | null) => {
  const s = (value ?? "").toString().trim();
  if (!s) return Number.POSITIVE_INFINITY;
  const matches = s.match(/\d+/g);
  if (!matches) return Number.POSITIVE_INFINITY;
  const nums = matches.map((seg) => parseInt(seg, 10)).filter((n) => Number.isFinite(n));
  return nums.length ? Math.min(...nums) : Number.POSITIVE_INFINITY;
};
const compareByCabinetValue = (a: any, b: any) => {
  const ar = cabinetRank(a?.cabinetNumber);
  const br = cabinetRank(b?.cabinetNumber);
  if (ar !== br) return ar - br;
  const as = (a?.cabinetNumber ?? '').toString();
  const bs = (b?.cabinetNumber ?? '').toString();
  if (as !== bs) return as.localeCompare(bs);
  return (a?.id || '').localeCompare(b?.id || '');
};

const CalendarWrapper = styled.div<{ theme?: CustomThemeType }>`
  & .rdrDefinedRangesWrapper {
    display: none;
  }
  & .rdrCalendarWrapper {
    background-color: ${({ theme }) => theme?.background};
    border: 2px solid ${({ theme }) => theme?.dividerColor};
  }
  & .rdrDateDisplayWrapper {
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrStartEdge,
  & .rdrInRange,
  & .rdrEndEdge,
  & .rdrSelected {
    color: ${({ theme }) => theme?.background} !important;
  }
  & .rdrDayNumber span {
    color: ${({ theme }) => theme?.foreground};
  }
  & .rdrDayNumber span::after {
    background: ${({ theme }) => theme?.foreground};
  }
  & .rdrMonthName,
  & .rdrWeekDay {
    color: ${({ theme }) => theme?.brand};
  }
  & .rdrDay {
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrNextPrevButton {
    color: ${({ theme }) => theme?.foreground};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrNextPrevButton i {
    color: ${({ theme }) => theme?.foreground};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrMonthAndYearPickers select {
    color: ${({ theme }) => theme?.brand};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrPprevButton i {
    border-color: transparent ${({ theme }) => theme?.brand} transparent
      transparent;
  }
  & .rdrNextButton i {
    border-color: transparent transparent transparent
      ${({ theme }) => theme?.brand};
  }
  & .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
    color: ${({ theme }) => theme?.foreground};
  }
`;

interface DraggableOrderAndItemsProps {
  order?: Order;
  orderSnapshot?: OrderSnapshot;
  isExpanded: boolean;
  orderIdx: number;
  selectedOrders: any;
  onToggleExpand: (orderId: string) => void;
  onOrderSelect: (orderId: string) => void;
  onItemSelect: (itemId: string) => void;
  selectedItems: any;
  disabled: boolean;
  dealerNameLookup?: Record<string, string>;
}
const DraggableOrderAndItems: React.FC<DraggableOrderAndItemsProps> = ({
  order,
  orderSnapshot,
  isExpanded,
  orderIdx,
  selectedOrders,
  onToggleExpand,
  onOrderSelect,
  selectedItems,
  onItemSelect,
  disabled,
  dealerNameLookup = {},
}) => {
  const theme = useTheme();
  const orderId = order?.id || orderSnapshot?.orderId || "unknown";
  const orderNumberFromOrder = (order as any)?.orderNumber;
  const orderNumberFromSnapshot =
    (orderSnapshot as any)?.orderNumber ||
    (orderSnapshot as any)?.preservedOrder?.orderNumber;
  const orderDisplayId =
    orderNumberFromOrder || orderNumberFromSnapshot || orderId;
  try {
    console.debug("[ManageShipments] order label sources", {
      orderId,
      orderNumberFromOrder,
      orderNumberFromSnapshot,
      display: orderDisplayId,
    });
  } catch {}
  const itemCount = order?.itemsSnapshot
    ? Object.keys(order.itemsSnapshot).length
    : orderSnapshot
      ? Object.keys(orderSnapshot.includedItemsSnapshot || {}).length
      : 0;
  const itemSnapshots =
    order?.itemsSnapshot || orderSnapshot?.includedItemsSnapshot || {};
  const orderDestinations =
    order?.destinations ||
    (orderSnapshot as any)?.destinations ||
      [];
  const normalizeDealerId = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  };
  const collectOrderDealerIds = (): string[] => {
    const seen = new Set<string>();
    const push = (value: unknown) => {
      const normalized = normalizeDealerId(value);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
    };
    const pushMany = (value: unknown) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => push(entry));
        return;
      }
      push(value);
    };
    pushMany((order as any)?.dealerIds);
    push((order as any)?.dealerId);
    pushMany((orderSnapshot as any)?.dealerIds);
    push((orderSnapshot as any)?.dealerId);
    const preservedOrder = (orderSnapshot as any)?.preservedOrder;
    if (preservedOrder && typeof preservedOrder === "object") {
      pushMany((preservedOrder as any)?.dealerIds);
      push((preservedOrder as any)?.dealerId);
    }
    return Array.from(seen);
  };
  const dealerDisplay = (() => {
    const dealers = collectOrderDealerIds();
    if (!dealers.length) return null;
    const labels = dealers
      .map((dealerId) => {
        if (!dealerId) return "";
        const friendly = dealerNameLookup?.[dealerId];
        if (typeof friendly === "string" && friendly.trim().length) {
          return friendly.trim();
        }
        return dealerId;
      })
      .filter((label) => label && label.trim().length > 0);
    if (!labels.length) return null;
    return Array.from(new Set(labels)).join(", ");
  })();
  const destinationDisplay = (() => {
    if (Array.isArray(orderDestinations) && orderDestinations.length > 0) {
      return Array.from(
        new Set(
          orderDestinations
            .map((dest) => (dest ? String(dest).trim() : ""))
            .filter((dest) => dest.length > 0)
        )
      ).join(", ");
    }
    const fallback = Object.values(itemSnapshots)
      .map((item: any) =>
        item?.destination ? String(item.destination).trim() : ""
      )
      .filter((dest) => dest.length > 0);
    if (fallback.length === 0) return null;
    return Array.from(new Set(fallback)).join(", ");
  })();
  return (
    <Draggable key={orderId} draggableId={`order-${orderId}`} index={orderIdx} isDragDisabled={disabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            background: snapshot.isDragging
              ? theme.palette.action.hover
              : "inherit",
            borderRadius: 8,
            marginBottom: 4,
          }}
        >
          {/* @ts-ignore */}
          <ListItem
            sx={{
              bgcolor: selectedOrders.includes(orderId)
                ? "action.selected"
                : "inherit",
              borderRadius: 1,
              mb: 0,
            }}
            {...provided.dragHandleProps}
            onClick={() => onToggleExpand(orderId)}
            button
          >
            {!disabled && (
              <Checkbox
                edge="end"
                checked={selectedOrders.includes(orderId)}
                onClick={(e) => {
                  e.stopPropagation();
                  onOrderSelect(orderId);
                }}
                tabIndex={-1}
                disableRipple
                sx={{ pr: 4 }}
              />
            )}
            <ListItemText
              primary={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: disabled ? "text.secondary" : "text.primary" }}
                  >
                    Order: {orderDisplayId}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ m: 0, lineHeight: 1.2 }}
                    >
                      {`Items: ${itemCount}`}
                    </Typography>
                    {destinationDisplay && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          m: 0,
                          lineHeight: 1.2,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                        title={destinationDisplay}
                      >
                        Destinations: {destinationDisplay}
                      </Typography>
                    )}
                    {dealerDisplay && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          m: 0,
                          lineHeight: 1.2,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                        title={dealerDisplay}
                      >
                        Dealer: {dealerDisplay}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            />
            {isExpanded ? <ExpandLess sx={{color: disabled ? "text.secondary" : "text.primary"}} /> : <ExpandMore sx={{color: disabled ? "text.secondary" : "text.primary"}} />}
          </ListItem>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Droppable droppableId={`items-${orderId}`} type="ITEM">
              {(itemProvided) => (
                <List
                  ref={itemProvided.innerRef}
                  {...itemProvided.droppableProps}
                  dense
                  sx={{ pl: 3, pt: 0.5 }}
                >
                  {Object.entries(itemSnapshots).sort((a,b) => compareByCabinetValue(a[1], b[1])).map(
                    ([itemKey, item]: [string, any], itemIdx: number) => {
                      const resolvedId = item?.id || itemKey;
                      const resolvedOrderId = item?.orderId || orderId;
                      if (!item?.id || !item?.orderId) {
                        console.debug("[ManageShipments] item fallback applied", {
                          orderId,
                          resolvedOrderId,
                          itemKey,
                          hasId: !!item?.id,
                          hasOrderId: !!item?.orderId,
                        });
                      }
                      return (
                        <Draggable
                          key={resolvedId}
                          draggableId={`item-${resolvedId}`}
                          index={itemIdx}
                          isDragDisabled={disabled}
                        >
                        {(itemDragProvided, itemSnapshot) => (
                          // @ts-ignore
                          <ListItem
                            ref={itemDragProvided.innerRef}
                            {...itemDragProvided.draggableProps}
                            {...itemDragProvided.dragHandleProps}
                            sx={{
                              bgcolor: selectedItems.includes(resolvedId)
                                ? "action.selected"
                                : "inherit",
                              borderRadius: 1,
                              mb: 0.5,
                              ml: 0,
                              pl: 2,
                              ...(itemSnapshot.isDragging && {
                                background: theme.palette.action.hover,
                              }),
                            }}
                            button
                          >
                            {!disabled && (
                              <Checkbox
                                edge="end"
                                checked={selectedItems.includes(resolvedId)}
                                onClick={() => onItemSelect(resolvedId)}
                                tabIndex={-1}
                                disableRipple
                                sx={{ pr: 4 }}
                              />
                            )}
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                    color: disabled ? "text.secondary" : "text.primary",
                                  }}
                                >
                                  {item.cabinetNumber && (
                                    <Typography variant="body2" fontWeight={700}>
                                      Cabinet # {item.cabinetNumber}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" color="text.secondary">
                                    {item.description || item.modelName || resolvedId}
                                  </Typography>
                                </Box>
                              }
                              // ID removed from UI per request
                              sx={{ color: disabled ? "text.secondary" : "text.primary" }}
                            />
                          </ListItem>
                        )}
                      </Draggable>
                      );
                    }
                  )}
                  {itemProvided.placeholder}
                </List>
              )}
            </Droppable>
          </Collapse>
        </div>
      )}
    </Draggable>
  );
};

const ManageShipments: React.FC = () => {
  const {
    unassignedOrders,
    shipments,
    dateRange,
    setDateRange,
    assignOrderToShipment,
    moveOrderBetweenShipments,
    unassignOrderFromShipment,
    changedItems,
    saveChanges,
    setShipmentTruck,
    changedShipments,
    setShipmentDrivers, // <-- Add this to your context/provider (see below)
    setShipmentDate, // <-- Add this
    isSaving,
    manufacturerNotificationEmail,
    manufacturerNotificationName,
  } = useShipmentManager();
  const userData = useUserData();
  const renderBottomSection =
    changedItems.length > 0 || Object.keys(changedShipments).length > 0;
  const { trucks, refresh: refreshTrucks } = useTrucksData();
  const { users, refresh: refreshUsers } = useUsersByManufacturer();
  const [dealerNameLookup, setDealerNameLookup] = useState<Record<string, string>>({});

  useEffect(() => {
    refreshTrucks();
    refreshUsers();
  }, []);

  useEffect(() => {
    let isActive = true;
    const loadDealers = async () => {
      if (!userData) {
        if (isActive) setDealerNameLookup({});
        return;
      }
      const db = getFirestore();
      try {
        if ((userData.isAdmin || userData.role === "manufacturer") && userData.manufacturerId) {
          const dealersRef = collection(db, "dealers");
          const q = query(
            dealersRef,
            where("manufacturerIds", "array-contains", userData.manufacturerId)
          );
          const snap = await getDocs(q);
          if (!isActive) return;
          const map: Record<string, string> = {};
          snap.forEach((docSnap) => {
            const data = docSnap.data() as { name?: string };
            const name = typeof data?.name === "string" ? data.name.trim() : "";
            map[docSnap.id] = name || docSnap.id;
          });
          setDealerNameLookup(map);
          return;
        }
        if (userData.role === "dealer" && userData.dealerId) {
          const dealerRef = doc(db, "dealers", userData.dealerId);
          const dealerSnap = await getDoc(dealerRef);
          if (!isActive) return;
          if (dealerSnap.exists()) {
            const data = dealerSnap.data() as { name?: string };
            const name = typeof data?.name === "string" ? data.name.trim() : "";
            setDealerNameLookup({ [dealerSnap.id]: name || dealerSnap.id });
          } else {
            setDealerNameLookup({});
          }
          return;
        }
        if (isActive) setDealerNameLookup({});
      } catch (error) {
        console.error("[ManageShipments] failed to load dealer directory", error);
        if (isActive) setDealerNameLookup({});
      }
    };
    loadDealers();
    return () => {
      isActive = false;
    };
  }, [userData?.dealerId, userData?.isAdmin, userData?.manufacturerId, userData?.role]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTruck, setEditingTruck] = useState<Truck | null>(null);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTruck, setMenuTruck] = useState<Truck | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingTruck, setDeletingTruck] = useState<Truck | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [shipmentDatePopoverIndex, setShipmentDatePopoverIndex] = useState<
    number | null
  >(null);
  //   const [dateRange, setDateRange] = React.useState([
  //     {
  //       startDate: new Date(),
  //       endDate: addDays(new Date(), 7),
  //       key: "selection",
  //     },
  //   ]);

  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedShipments, setExpandedShipments] = useState<
    Record<string, boolean>
  >({});

  // Popover state for date range picker
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Custom styles using theme
  const customStyles = {} as React.CSSProperties;

  // Helper to format the selected date range for the button label
  const formatRange = () => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return "Select Date Range";
    return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
  };

  // Handle expand/collapse
  const handleToggleExpand = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  // Shipment expand/collapse
  const handleToggleShipmentExpand = (shipmentId: string) => {
    setExpandedShipments((prev) => ({
      ...prev,
      [shipmentId]: !prev[shipmentId],
    }));
  };

  // Handle order select
  const handleOrderSelect = (orderId: string) => {
    const prevSelected = selectedOrders.includes(orderId);
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
    Object.keys(
      unassignedOrders.find((o) => o.id === orderId)?.itemsSnapshot || {}
    ).forEach((itemId) => {
      setItemSelected(itemId, !prevSelected);
    });
  };

  const setItemSelected = (itemId: string, selected: boolean) => {
    setSelectedItems((prev) =>
      selected ? [...prev, itemId] : prev.filter((id) => id !== itemId)
    );
  };

  // Handle item select
  const handleItemSelect = (itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Helpers to parse draggable/droppable IDs robustly (order IDs can contain '-')
  const parseDraggableId = (id: string): { type: string; id?: string } => {
    if (id.startsWith('order-')) return { type: 'order', id: id.slice('order-'.length) };
    if (id.startsWith('item-')) return { type: 'item', id: id.slice('item-'.length) };
    return { type: id };
  };
  const parseDroppableId = (id: string): { type: string; id?: string } => {
    if (id === 'orders') return { type: 'orders' };
    if (id.startsWith('shipment-')) return { type: 'shipment', id: id.slice('shipment-'.length) };
    if (id.startsWith('items-')) return { type: 'items', id: id.slice('items-'.length) };
    return { type: id };
  };

  // Drag and drop handlers (for now, just reorder in-place)
  const onDragEnd: OnDragEndResponder<string> = (result) => {
    const drag = parseDraggableId(result.draggableId);
    const src = parseDroppableId(result.source.droppableId);
    const dest = result.destination ? parseDroppableId(result.destination.droppableId) : null;
    if (!dest) return;

    // Debug logging for drag events
    console.debug('[ManageShipments] onDragEnd', {
      draggableType: drag.type,
      dropType: dest.type,
      sourceType: src.type,
      orderId: drag.id,
      destShipmentId: dest.type === 'shipment' ? dest.id : undefined,
      srcShipmentId: src.type === 'shipment' ? src.id : undefined,
    });
    if (dest.type === 'shipment' && dest.id) {
      const destShipment = shipments.find(s => s.id === dest.id);
      if (destShipment) {
        const hasProcessedItems = Object.values(destShipment.ordersSnapshot).some(orderSnap =>
          Object.values(orderSnap.includedItemsSnapshot || {}).some((item: any) => item.currentProcessed)
        );
        console.debug('[ManageShipments] drop target status', { destShipmentId: dest.id, hasProcessedItems });
      }
    }

    // From Unassigned Orders -> Shipment (assign)
    if (drag.type === 'order' && dest.type === 'shipment' && src.type === 'orders') {
      const shipmentId = dest.id;
      const orderId = drag.id;
      if (!shipmentId || !orderId) return;
      console.debug('[ManageShipments] assignOrderToShipment ->', { orderId, shipmentId });
      assignOrderToShipment(orderId, shipmentId);
      return;
    }

    // From Shipment -> Shipment (move)
    if (drag.type === 'order' && dest.type === 'shipment' && src.type === 'shipment') {
      const shipmentId = dest.id;
      const orderId = drag.id;
      const sourceId = src.id;
      if (!shipmentId || !orderId || !sourceId) return;
      if (shipmentId === sourceId) return;
      console.debug('[ManageShipments] moveOrderBetweenShipments ->', { orderId, sourceId, shipmentId });
      moveOrderBetweenShipments(orderId, sourceId, shipmentId);
      return;
    }

    // From Shipment -> Unassigned Orders (unassign)
    if (drag.type === 'order' && dest.type === 'orders' && src.type === 'shipment') {
      const orderId = drag.id;
      const shipmentId = src.id;
      if (!orderId || !shipmentId) return;
      console.debug('[ManageShipments] unassignOrderFromShipment ->', { orderId, shipmentId });
      unassignOrderFromShipment(orderId, shipmentId);
      return;
    }
  };

  return (
    <DashboardLayout>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{ display: "flex", flexDirection: "column", pl: 3.5, pt: 1 }}>
          <Box sx={{ width: { xs: "100%", lg: "100%" }, maxWidth: "1400px" }}>
            <Box
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "primary.main",
                }}
              >
                <ViewInArIcon
                  sx={{
                    fontSize: "2.5rem",
                    bottom: "2px",
                    position: "relative",
                  }}
                  color="primary"
                />
                Manage Shipments
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "flex-start", sm: "flex-end" },
                  gap: 0.5,
                  mt: { xs: 1.5, sm: 0 },
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Notification Contact
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {manufacturerNotificationEmail || "Not configured"}
                </Typography>
                {manufacturerNotificationName ? (
                  <Typography variant="caption" color="text.secondary">
                    {manufacturerNotificationName}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "70vh",
              width: "100%",
              pt: 0,
            }}
          >
            <Box sx={{ width: "33%", pt: 0 }}>
              <Typography variant="h5" sx={{ mb: 0.5 }} color="primary">
                Unassigned Orders
              </Typography>
              <Box
                sx={{
                  height: "67vh",
                  overflowY: "auto",
                  border: 2,
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  borderColor: "divider",
                  p: 1,
                }}
              >
                <Droppable droppableId="orders" type="ORDER">
                  {(provided) => (
                    <List
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{ width: "100%", pt: 0, height: '100%' }}
                    >
                      {unassignedOrders.map((order, orderIdx) => (
                        <DraggableOrderAndItems
                          order={order}
                          dealerNameLookup={dealerNameLookup}
                          key={order.id}
                          orderIdx={orderIdx}
                          onItemSelect={handleItemSelect}
                          selectedItems={selectedItems}
                          onOrderSelect={handleOrderSelect}
                          selectedOrders={selectedOrders}
                          isExpanded={!!expandedOrders[order.id]}
                          onToggleExpand={handleToggleExpand}
                          disabled={false}
                        />
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </Box>
            </Box>
            <Box sx={{ width: "64%", pt: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h5" sx={{ mb: 0.5 }} color="primary">
                  Shipments
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CalendarMonthIcon />}
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setShipmentDatePopoverIndex(-1);
                    }}
                    sx={{
                      backgroundColor: "background.paper",
                      borderColor: "divider",
                      color: "primary.main",
                      position: "relative",
                      bottom: ".25rem",
                      "&:hover, &:active": {
                        backgroundColor: "background.paper",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    {formatRange()}
                  </Button>
                  <Popover
                    open={shipmentDatePopoverIndex === -1 && Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <CalendarWrapper>
                      <DateRangePicker
                        onChange={(item: any) => {
                          setDateRange(item.range1);
                        }}
                        showPreview={false}
                        moveRangeOnFirstSelection={false}
                        months={2}
                        ranges={[dateRange]}
                        showDateDisplay={false}
                        staticRanges={[]}
                        inputRanges={[]}
                        direction="horizontal"
                      />
                    </CalendarWrapper>
                  </Popover>
                </Box>
              </Box>
              <Box
                sx={{
                  height: "67vh",
                  overflowY: "auto",
                  border: 2,
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  borderColor: "divider",
                }}
              >
                <List sx={{ width: "100%", p: 0 }}>
                  {shipments.map((shipment, idx) => {
                    const hasProcessedItems = Object.values(shipment.ordersSnapshot).some(orderSnap =>
                      Object.values(orderSnap.includedItemsSnapshot || {}).some(item => item.currentProcessed)
                    );
                    return (
                    <Droppable
                      key={shipment.id}
                      droppableId={`shipment-${shipment.id}`}
                      type="ORDER"
                      isDropDisabled={hasProcessedItems}
                    >
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {/* @ts-ignore */}
                          <ListItem
                            sx={{
                              bgcolor: expandedShipments[shipment.id]
                                ? "action.selected"
                                : "inherit",
                              borderRadius: 1,
                              mb: 0,
                              cursor: "pointer",
                              height: 100,
                              borderColor: "divider",
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                              pl: 4,
                              pr: 4,
                            }}
                            button
                            onClick={() => handleToggleShipmentExpand(shipment.id)}
                          >
                            <TruckIcon sx={{color: "primary.main"}}/>
                            <ListItemText
                              sx={{ color: "text.primary", ml: 3 }}
                              primary={
                                <Typography variant="h6" color="primary.main">
                                  {shipment.id}{" "}
                                  <Typography component="span" sx={{ color: hasProcessedItems ? "error.main" : "text.secondary" }} variant="subtitle2">
                                    ({hasProcessedItems ? "Locked - In Progress" : "pending"})
                                  </Typography>
                                </Typography>
                              }
                              secondary={`Shipment Date: ${shipment.shipmentDate.toDate().toLocaleDateString()}`}
                            />
                            {expandedShipments[shipment.id] ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </ListItem>
                          <Collapse
                            in={expandedShipments[shipment.id]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ display: "flex", gap: 2, px: 2, py: 2 }}>
                              <Box
                                sx={{
                                  flex: 3,
                                }}
                              >
                                {/* Left: Orders/items list (no longer a separate Droppable) */}
                                <Typography
                                  variant="subtitle1"
                                  color="text.secondary"
                                  sx={{ m: 0, p: 0 }}
                                >
                                  Orders
                                  {' '}
                                  <Typography
                                    variant="caption" color="error.main"
                                  >
                                    {hasProcessedItems && "(Locked - In Progress)"}
                                  </Typography>
                                </Typography>
                                <Box
                                  sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    minHeight: 177,
                                    backgroundColor: "background.default",
                                    p: 1,
                                    mr: 2,
                                  }}
                                >
                                  <List dense sx={{ minHeight: 60, p: 0 }}>
                                    {Object.entries(
                                      shipment.ordersSnapshot
                                    ).map(([orderId, orderSnap], idx) => {
                                      const orderSnapToUse = {
                                        ...orderSnap,
                                        orderId,
                                      };
                                      return (
                                        <DraggableOrderAndItems
                                          disabled={hasProcessedItems}
                                          key={orderId}
                                          orderSnapshot={orderSnapToUse}
                                          dealerNameLookup={dealerNameLookup}
                                          isExpanded={expandedOrders[orderId]}
                                          orderIdx={idx}
                                          selectedOrders={selectedOrders}
                                          onToggleExpand={handleToggleExpand}
                                          onOrderSelect={handleOrderSelect}
                                          selectedItems={selectedItems}
                                          onItemSelect={handleItemSelect}
                                        />
                                      );
                                    })}
                                    {provided.placeholder}
                                  </List>
                                </Box>
                              </Box>
                              {/* Right: Shipment info */}
                              <Box
                                sx={{
                                  flex: 2,
                                  borderRadius: 2,
                                  minHeight: 120,
                                  p: 0,
                                  mt: 0,
                                }}
                              >
                                {/* Truck Dropdown */}
                                <Typography variant="subtitle1" color="text.secondary">
                                  Truck:
                                </Typography>
                                <Select
                                  size="small"
                                  value={shipment.truckId || ""}
                                  onChange={(e: any) => {
                                    setShipmentTruck(
                                      shipment.id,
                                      e.target.value as string
                                    );
                                  }}
                                  displayEmpty
                                  sx={{ minWidth: 160, width: "100%", mt: 0, color: "primary.main" }}
                                  disabled={hasProcessedItems}
                                >
                                  <MenuItem value="">
                                    <em>Unassigned</em>
                                  </MenuItem>
                                  {trucks.map((truck) => (
                                    <MenuItem
                                      key={truck.licensePlate}
                                      value={truck.licensePlate}
                                    >
                                      {truck.licensePlate}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {/* Drivers MultiSelect Dropdown */}
                                <FormControl
                                  sx={{ minWidth: 160, mt: 0.5, width: "100%" }}
                                  size="small"
                                >
                                  <Typography variant="subtitle1" color="text.secondary">
                                    Drivers:
                                  </Typography>
                                  <Select
                                    sx={{
                                      minWidth: 160,
                                      width: "100%",
                                      color: "primary.main"
                                    }}
                                    multiple
                                    value={shipment.drivers || []}
                                    onChange={(e) => {
                                      const value = e.target.value as string[];
                                      setShipmentDrivers(shipment.id, value);
                                    }}
                                    input={<OutlinedInput />}
                                    displayEmpty
                                    disabled={hasProcessedItems}
                                    renderValue={(selected) => {
                                      return (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                          }}
                                        >
                                          {selected.length === 0 && (
                                            <em>Unassigned</em>
                                          )}
                                          {(selected as string[]).map(
                                            (driverId) => {
                                              const user = users.find(
                                                (u) => u.email === driverId
                                              );
                                              return (
                                                <Chip
                                                  key={driverId}
                                                  sx={{
                                                    backgroundColor:
                                                      "background.default",
                                                    color: "primary.main",
                                                  }}
                                                  label={
                                                    user ? user.name : driverId
                                                  }
                                                  size="small"
                                                />
                                              );
                                            }
                                          )}
                                        </Box>
                                      );
                                    }}
                                  >
                                    {users
                                      .filter((user) => (user.role || "").toLowerCase() === "driver")
                                      .map((user) => (
                                        <MenuItem key={user.email} value={user.email}>
                                          {user.name}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                                <Typography variant="subtitle1" color="text.secondary">
                                  Date:
                                </Typography>
                                <Button
                                  disabled={hasProcessedItems}
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<CalendarMonthIcon sx={{ color: hasProcessedItems ? "text.secondary" : "primary.main" }} />}
                                  onClick={(e) => {
                                    setAnchorEl(e.currentTarget);
                                    setShipmentDatePopoverIndex(idx);
                                  }}
                                  sx={{
                                    backgroundColor: "background.paper",
                                    borderColor: "divider",
                                    color: "primary.main",
                                    position: "relative",
                                    bottom: ".25rem",
                                    "&:hover, &:active": {
                                      backgroundColor: "background.paper",
                                      borderColor: "primary.main",
                                    },
                                    mt: 0.5,
                                  }}
                                >
                                  {shipment.shipmentDate
                                    ? shipment.shipmentDate
                                        .toDate()
                                        .toLocaleDateString()
                                    : "Select Date"}
                                </Button>
                                <Popover
                                  open={
                                    shipmentDatePopoverIndex === idx &&
                                    Boolean(anchorEl)
                                  }
                                  anchorEl={anchorEl}
                                  onClose={() => setAnchorEl(null)}
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                  }}
                                  // transformOrigin={{
                                  //   vertical: "top",
                                  //   horizontal: "right",
                                  // }}
                                >
                                  <CalendarWrapper>
                                    <Calendar
                                      onChange={(item: any) => {
                                        // item is a Date object from react-date-range Calendar
                                        setShipmentDate(shipment.id, item);
                                        setAnchorEl(null);
                                      }}
                                      date={shipment.shipmentDate.toDate()}
                                      showPreview={false}
                                      months={1}
                                      showDateDisplay={false}
                                      direction="horizontal"
                                    />
                                  </CalendarWrapper>
                                </Popover>
                                {/* Add more shipment info as needed */}
                              </Box>
                            </Box>
                          </Collapse>
                          <Divider />
                        </div>
                      )}
                    </Droppable>
                    );
})}
                </List>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              pt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              opacity: renderBottomSection ? 1 : 0,
              pointerEvents: renderBottomSection ? "auto" : "none",
              transition: "opacity 0.3s ease",
            }}
          >
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={() => saveChanges()}
              disabled={isSaving}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </DragDropContext>
    </DashboardLayout>
  );
};

export default ManageShipments;

